import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { InMsg, OutMsg } from "../../app/backendTypes";
import { getToken } from "../../app/auth";

// This is not pretty but it works.
// If someone knows how to create a proxy for websockets through the dev server, please let me know.
const ws_url =
  window.location.protocol === "https:"
    ? `wss://${window.location.host}/ws`
    : "ws://localhost:3001/ws";

enum ActionKind {
  ConfluenceBulkOffboarding = "ConfluenceBulkOffboarding",
  JiraBulkOffboarding = "JiraBulkOffboarding",
  JiraBulkOnboarding = "JiraBulkOnboarding",
  LucidBulkOffboarding = "LucidBulkOffboarding",
  PolarisBulkOffboarding = "PolarisBulkOffboarding",
  MimBulkOnboarding = "MimBulkOnboarding",
}

export default function Actions() {
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState<string[]>([]);
  const [kind, setKind] = useState<ActionKind | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  const token = getToken();

  const createSocketAndRun = () => {
    if (token === null) return;

    const start = new Date();

    setLoading(true);
    setOutput(() => [`Starting at ${start.toLocaleTimeString()}`]);

    const socket = new WebSocket(ws_url);

    socket.onopen = () => {
      setOutput((output) => [...output, "Sending login message"]);

      const msg = { kind: "Login", inner: { token } } as InMsg;

      socket.send(JSON.stringify(msg));
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data) as OutMsg;

      if (msg.kind === "LoggedIn") {
        setOutput((output) => [...output, "Logged in"]);

        const msg = {
          kind,
          inner: convertInputToInner(kind, input),
        } as InMsg;

        socket.send(JSON.stringify(msg));
      }

      if (msg.kind === "Output") {
        setOutput((output) => [...output, msg.inner.content]);
      }

      if (msg.kind === "Done") {
        const end = new Date();
        const elapsed = end.getTime() - start.getTime();

        setOutput((output) => [
          ...output,
          `Done at ${end.toLocaleTimeString()}`,
          `Elapsed time: ${msToHumanReadable(elapsed)}`,
        ]);

        socket.close();
        setLoading(false);
      }
    };

    socket.onerror = () => {
      setLoading(false);
    };

    socket.onclose = () => {
      setLoading(false);
    };
  };

  useEffect(() => {
    if (outputRef.current !== null) {
      outputRef.current.scroll(0, outputRef.current.scrollHeight);
    }
  }, [output, outputRef]);

  const handleActionKindChange = (event: SelectChangeEvent) => {
    const kind = event.target.value as ActionKind;
    setKind(kind);
  };

  return (
    <>
      <TextField
        label={inputLabel(kind)}
        value={input}
        fullWidth
        multiline
        maxRows={5}
        onChange={(e) => setInput(e.target.value)}
        disabled={loading}
        placeholder={inputPlaceholder(kind)}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          gap: 2,
          justifyContent: "center",
        }}
      >
        <FormControl fullWidth sx={{ maxWidth: "sm" }}>
          <InputLabel id="action-kind">Action Kind</InputLabel>
          <Select
            disabled={loading}
            labelId="action-kind"
            value={kind || ""}
            label="Action Kind"
            onChange={handleActionKindChange}
          >
            {Object.keys(ActionKind).map((k, i) => (
              <MenuItem key={i} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={() => createSocketAndRun()}
          disabled={loading || kind === null}
        >
          Run
        </Button>
      </Box>

      <Box
        ref={outputRef}
        sx={{
          position: "absolute",
          width: "90%",
          bottom: "5%",
          left: "5%",
          right: "5%",
          top: "50%",
          overflow: "scroll",
          border: "1px solid",
          borderRadius: 2,
          borderColor: loading ? "primary.main" : "divider",
          padding: "1em",
        }}
      >
        {output.map((l, i) => (
          <Typography
            variant="body2"
            key={i}
            color={l.startsWith("Error:") ? "error" : "text.secondary"}
          >
            {l}
          </Typography>
        ))}
      </Box>
    </>
  );
}

function convertInputToInner(kind: string | null, input: string) {
  let inner;

  switch (kind) {
    case "ConfluenceBulkOffboarding":
    case "JiraBulkOffboarding":
    case "JiraBulkOnboarding":
    case "LucidBulkOffboarding":
    case "PolarisBulkOffboarding": {
      inner = { users: input.split(",") };
      break;
    }

    case "MimBulkOnboarding": {
      const parts = input.split(",");
      const group_id = parts[0];
      const users = parts.slice(1);

      inner = { group_id, users };

      break;
    }

    default: {
      inner = null;
    }
  }

  return inner;
}

function inputLabel(kind: string | null) {
  let label;

  switch (kind) {
    case "ConfluenceBulkOffboarding": {
      label = "Emails only (comma separated)";
      break;
    }

    case "JiraBulkOffboarding": {
      label = "Usernames only (comma separated)";
      break;
    }

    case "JiraBulkOnboarding": {
      label = "Emails (comma separated)";
      break;
    }
      
    case "LucidBulkOffboarding": {
      label = "Emails (comma separated)";
      break;
    }

    case "PolarisBulkOffboarding": {
      label = "Usernames and emails (comma separated)";
      break;
    }

    case "MimBulkOnboarding": {
      label = "Group ID, emails (comma separated)";
      break;
    }

    default: {
      label = "Input";
    }
  }

  return label;
}

function inputPlaceholder(kind: string | null) {
  let placeholder;

  switch (kind) {
    case "ConfluenceBulkOffboarding": {
      placeholder = "john.doe@email.com,jane.doe@other.com";
      break;
    }

    case "PolarisBulkOffboarding": {
      placeholder = "john.doe,jane.doe@email.com";
      break;
    }

    case "JiraBulkOffboarding": {
      placeholder = "john.doe,jane.doe";
      break;
    }

    case "JiraBulkOnboarding": {
      placeholder = "john.doe@email.com,jane.doe@other.com";
      break;
    }
    
    case "LucidBulkOffboarding": {
      placeholder = "john.doe@email.com,jane.doe@other.com";
      break;
    }

    case "MimBulkOnboarding": {
      placeholder =
        "1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4cdd,john.doe@email.com,jane.doe@other.com";
      break;
    }

    default: {
      placeholder = "";
    }
  }

  return placeholder;
}

function msToHumanReadable(ms: number) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  const secondsLeft = seconds % 60;
  const minutesLeft = minutes % 60;

  let result = "";

  if (hours > 0) {
    result += `${hours}h `;
  }

  if (minutesLeft > 0) {
    result += `${minutesLeft}m `;
  }

  result += `${secondsLeft}s`;

  return result;
}
